import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FaRedoAlt, 
  FaTrophy, 
  FaKeyboard, 
  FaHistory, 
  FaSignOutAlt, 
  FaTimes, 
  FaArrowLeft,
  FaSlidersH,
  FaCalendarAlt,
  FaClock
} from 'react-icons/fa';
import { 
  auth, 
  googleProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut, 
  onAuthStateChanged, 
  db, 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  limit, 
  where,
  Timestamp,
  serverTimestamp 
} from './firebase';
import './App.scss';

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.split('');

const formatTime = (time) => {
  if (time === null || time === undefined) return '--:--';
  const seconds = Math.floor(time / 1000);
  const milliseconds = Math.floor((time % 1000) / 10);
  return `${seconds}.${milliseconds.toString().padStart(2, '0')}s`;
};

// Format date helper for Leaderboard entries
const formatLeaderboardDate = (createdAt, period) => {
  if (!createdAt) return 'Recently';
  let dateObj;
  if (typeof createdAt.toDate === 'function') {
    dateObj = createdAt.toDate();
  } else if (createdAt.seconds) {
    dateObj = new Date(createdAt.seconds * 1000);
  } else {
    dateObj = new Date(createdAt);
  }

  if (isNaN(dateObj.getTime())) return 'Recently';

  if (period === 'all') {
    // All Time: only list the date
    return dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } else {
    // This Week or Today: list date and time
    return dateObj.toLocaleString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
};

function App() {
  // Navigation & UI state
  const [activeTab, setActiveTab] = useState('type'); // 'type' | 'leaderboard' | 'keybinds'
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all'); // 'all' | 'week' | 'today'

  // Auth Modal State
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authIsSignUp, setAuthIsSignUp] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authDisplayName, setAuthDisplayName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Keybinds state
  const [keybinds, setKeybinds] = useState(() => {
    try {
      const saved = localStorage.getItem('alphabetype_keybinds');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return {
            reset: typeof parsed.reset === 'string' ? parsed.reset : 'Enter',
            backspace: typeof parsed.backspace === 'string' ? parsed.backspace : 'Backspace'
          };
        }
      }
    } catch (e) {}
    return { reset: 'Enter', backspace: 'Backspace' };
  });
  const [listeningForBind, setListeningForBind] = useState(null); // 'reset' | 'backspace' | null

  // Typing test state
  const [typedChars, setTypedChars] = useState([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [stopwatch, setStopwatch] = useState(0);
  const [personalBest, setPersonalBest] = useState(() => {
    const item = localStorage.getItem('alphabetype_personal_best');
    return item ? JSON.parse(item) : null;
  });

  // Recent scores session log (stored in sessionStorage + component state)
  const [recentScores, setRecentScores] = useState(() => {
    const saved = sessionStorage.getItem('alphabetype_session_scores');
    return saved ? JSON.parse(saved) : [];
  });

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Refs
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Listen to Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);

      // Reset recent session run log on auth state change (sign in or sign out)
      setRecentScores([]);
      sessionStorage.removeItem('alphabetype_session_scores');

      if (currentUser) {
        // Sync user profile to Firestore
        try {
          await setDoc(doc(db, 'alphabetype_users', currentUser.uid), {
            uid: currentUser.uid,
            displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous Player',
            photoURL: currentUser.photoURL || '',
            lastActive: serverTimestamp()
          }, { merge: true });
        } catch (e) {
          console.error("Error updating user record:", e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch Leaderboard based on period ('all' | 'week' | 'today')
  const fetchLeaderboard = useCallback(async (period) => {
    setLeaderboardLoading(true);
    try {
      let q;
      const now = new Date();
      
      if (period === 'today') {
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        q = query(
          collection(db, 'alphabetype_scores'),
          where('createdAt', '>=', Timestamp.fromDate(startOfToday)),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
      } else if (period === 'week') {
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        q = query(
          collection(db, 'alphabetype_scores'),
          where('createdAt', '>=', Timestamp.fromDate(startOfWeek)),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
      } else {
        q = query(
          collection(db, 'alphabetype_scores'),
          orderBy('timeMs', 'asc'),
          limit(100)
        );
      }

      const snapshot = await getDocs(q);
      const scores = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Group by user ID to show each user's single best score for the given timeframe
      const userBestMap = new Map();
      scores.forEach(score => {
        const existing = userBestMap.get(score.userId);
        if (!existing || score.timeMs < existing.timeMs) {
          userBestMap.set(score.userId, score);
        }
      });

      const uniqueLeaderboard = Array.from(userBestMap.values()).sort((a, b) => a.timeMs - b.timeMs);
      setLeaderboard(uniqueLeaderboard);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
    } finally {
      setLeaderboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      fetchLeaderboard(leaderboardPeriod);
    }
  }, [activeTab, leaderboardPeriod, fetchLeaderboard]);

  // Auth Actions
  const handleGoogleSignIn = async () => {
    setAuthError('');
    setAuthSubmitting(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setShowAuthModal(false);
    } catch (err) {
      setAuthError(err.message || 'Google sign-in failed');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleEmailAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSubmitting(true);

    try {
      if (authIsSignUp) {
        const res = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        const name = authDisplayName.trim() || authEmail.split('@')[0] || 'User';
        await updateProfile(res.user, { displayName: name });
        await setDoc(doc(db, 'alphabetype_users', res.user.uid), {
          uid: res.user.uid,
          displayName: name,
          photoURL: '',
          lastActive: serverTimestamp()
        }, { merge: true });
      } else {
        await signInWithEmailAndPassword(auth, authEmail, authPassword);
      }
      setShowAuthModal(false);
      setAuthEmail('');
      setAuthPassword('');
      setAuthDisplayName('');
    } catch (err) {
      setAuthError(err.message || 'Authentication failed');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign out failed:", err);
    }
  };

  // Stopwatch timer logic
  useEffect(() => {
    if (running) {
      startTimeRef.current = Date.now() - stopwatch;
      timerRef.current = setInterval(() => {
        setStopwatch(Date.now() - startTimeRef.current);
      }, 10);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  // Reset test
  const resetTest = useCallback(() => {
    setRunning(false);
    setFinished(false);
    setStopwatch(0);
    setTypedChars([]);
  }, []);

  // Save score on test completion
  const handleTestCompletion = useCallback(async (finalTime) => {
    setRunning(false);
    setFinished(true);

    // Save to local personal best
    if (!personalBest || finalTime < personalBest) {
      setPersonalBest(finalTime);
      localStorage.setItem('alphabetype_personal_best', JSON.stringify(finalTime));
    }

    // Save to session recent scores (ordered from shortest time to longest time)
    const newSessionScore = {
      id: Date.now(),
      timeMs: finalTime,
      date: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      isUser: !!user
    };

    setRecentScores(prev => {
      const updated = [...prev, newSessionScore].sort((a, b) => a.timeMs - b.timeMs).slice(0, 15);
      sessionStorage.setItem('alphabetype_session_scores', JSON.stringify(updated));
      return updated;
    });

    // Save to Firebase if signed in
    if (user) {
      try {
        await addDoc(collection(db, 'alphabetype_scores'), {
          userId: user.uid,
          displayName: user.displayName || user.email?.split('@')[0] || 'Anonymous Player',
          photoURL: user.photoURL || '',
          timeMs: finalTime,
          createdAt: serverTimestamp()
        });
        if (activeTab === 'leaderboard') {
          fetchLeaderboard(leaderboardPeriod);
        }
      } catch (err) {
        console.error("Error saving score to Firebase:", err);
      }
    }
  }, [user, personalBest, activeTab, leaderboardPeriod, fetchLeaderboard]);

  // Keydown Handler for Typing & Keybinds
  useEffect(() => {
    const handleKeyDown = (e) => {
      // If listening for bind on keybinds page
      if (listeningForBind) {
        e.preventDefault();
        const keyName = e.key === ' ' ? 'Space' : e.key;
        setKeybinds(prev => {
          const updated = { ...prev, [listeningForBind]: keyName };
          localStorage.setItem('alphabetype_keybinds', JSON.stringify(updated));
          return updated;
        });
        setListeningForBind(null);
        return;
      }

      // Ignore input when auth modal is open
      if (showAuthModal) return;

      // Handle Reset Keybind
      const resetKeyStr = String(keybinds?.reset || 'Enter');
      const resetKeyLower = resetKeyStr.toLowerCase();
      const eventKeyLower = (e.key || '').toLowerCase();
      const eventCodeLower = (e.code || '').toLowerCase();
      const isResetKey = (
        eventKeyLower === resetKeyLower ||
        eventCodeLower === resetKeyLower ||
        (resetKeyStr === 'Space' && (e.key === ' ' || e.code === 'Space')) ||
        (resetKeyStr === 'Enter' && e.key === 'Enter') ||
        (resetKeyStr === 'Tab' && e.key === 'Tab') ||
        (resetKeyStr === 'Escape' && e.key === 'Escape')
      );

      if (isResetKey) {
        e.preventDefault();
        resetTest();
        return;
      }

      // Handle Backspace / Delete Keybind
      if (e.key === keybinds.backspace || e.key === 'Backspace' || e.key === 'Delete') {
        if (!finished && typedChars.length > 0) {
          e.preventDefault();
          setTypedChars(prev => prev.slice(0, -1));
        }
        return;
      }

      // Ignore non-single character keys (Shift, Alt, Meta, CapsLock, Tab, etc.)
      if (e.key.length !== 1 || e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }

      // Only accept lower/upper case letters for typing test
      const typedChar = e.key.toLowerCase();
      if (!/^[a-z]$/.test(typedChar)) {
        return;
      }

      if (finished || activeTab !== 'type') return;

      setTypedChars(prev => {
        // Prevent typing beyond alphabet length once completed
        if (prev.length >= 26) return prev;

        const nextTyped = [...prev, typedChar];

        // Start timer when typing first letter ('a')
        if (prev.length === 0) {
          setRunning(true);
        }

        // Check if alphabet completed correctly
        if (nextTyped.length === 26) {
          const isAllCorrect = nextTyped.every((char, idx) => char === ALPHABET[idx]);
          if (isAllCorrect) {
            const exactFinalTime = Date.now() - (startTimeRef.current || Date.now());
            setStopwatch(exactFinalTime);
            setTimeout(() => handleTestCompletion(exactFinalTime), 0);
          }
        }

        return nextTyped;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keybinds, listeningForBind, showAuthModal, finished, typedChars, activeTab, resetTest, handleTestCompletion]);

  // Compute status for each letter A-Z
  let firstWrongIdx = -1;
  for (let i = 0; i < typedChars.length; i++) {
    if (typedChars[i] !== ALPHABET[i]) {
      firstWrongIdx = i;
      break;
    }
  }

  return (
    <div className="alphabetype-root">
      {/* Top Navigation Bar */}
      <nav className="navbar">
        <div className="nav-brand" onClick={() => setActiveTab('type')}>
          <span className="brand-logo">A-Z</span>
          <span className="brand-title">AlphabeType</span>
        </div>

        <div className="nav-links">
          <button 
            className={`nav-btn ${activeTab === 'type' ? 'active' : ''}`} 
            onClick={() => setActiveTab('type')}
          >
            <FaKeyboard className="nav-icon" /> Practice
          </button>
          <button 
            className={`nav-btn ${activeTab === 'leaderboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('leaderboard')}
          >
            <FaTrophy className="nav-icon" /> Leaderboard
          </button>
          <button 
            className={`nav-btn ${activeTab === 'keybinds' ? 'active' : ''}`}
            onClick={() => setActiveTab('keybinds')}
          >
            <FaSlidersH className="nav-icon" /> Keybinds
          </button>
        </div>

        <div className="nav-auth">
          {authLoading ? (
            <div className="auth-spinner"></div>
          ) : user ? (
            <div className="user-profile">
              <AvatarImage photoURL={user.photoURL} displayName={user.displayName} />
              <span className="user-name">{user.displayName || user.email?.split('@')[0] || 'User'}</span>
              <button className="signout-btn-full" onClick={handleSignOut}>
                <FaSignOutAlt className="signout-icon" /> Sign Out
              </button>
            </div>
          ) : (
            <button className="google-signin-btn" onClick={() => { setAuthError(''); setShowAuthModal(true); }}>
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="main-content">
        {activeTab === 'type' && (
          <div className="practice-view">
            {/* Header Timer Bar */}
            <div className="timer-header">
              <div className="stat-card">
                <span className="stat-label">TIME</span>
                <h1 className="stat-value">{formatTime(stopwatch)}</h1>
              </div>

              <button className="reset-btn" onClick={resetTest} title={`Reset (${keybinds.reset})`}>
                <FaRedoAlt className="reset-icon" />
              </button>

              <div className="stat-card">
                <span className="stat-label">PERSONAL BEST</span>
                <h1 className="stat-value">{formatTime(personalBest)}</h1>
              </div>
            </div>

            {/* Instruction Banner */}
            <div className="instruction-box">
              <p>
                Start typing from <strong>'a'</strong> to <strong>'z'</strong>. Timer starts automatically.
              </p>
              <div className="keybind-pills">
                <span className="pill">Reset: <code>{keybinds.reset}</code></span>
                <span className="pill">Delete/Backspace: <code>{keybinds.backspace}</code></span>
              </div>
            </div>

            {/* Alphabet Keyboard Grid */}
            <div className="alphabet-grid">
              {ALPHABET.map((letter, idx) => {
                let statusClass = '';
                const typedChar = typedChars[idx];

                if (typedChar !== undefined) {
                  if (typedChar === letter && (firstWrongIdx === -1 || idx < firstWrongIdx)) {
                    statusClass = 'complete';
                  } else {
                    statusClass = 'wrong';
                  }
                } else if (idx === typedChars.length && !finished) {
                  statusClass = 'current';
                }

                return (
                  <div key={letter} className={`letter-box ${statusClass}`}>
                    <span className="letter-char">{letter.toUpperCase()}</span>
                    {typedChar !== undefined && typedChar !== letter && (
                      <span className="typed-err-sub">{typedChar}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Wrong Key Warning Notice */}
            {firstWrongIdx !== -1 && !finished && (
              <div className="error-alert">
                <span>Mistake made! Press <code>{keybinds.backspace}</code> to go back and correct it.</span>
              </div>
            )}

            {/* Session Recent Scores Log */}
            <div className="recent-scores-container">
              <div className="recent-scores-header">
                <h3><FaHistory /> Session Score Log</h3>
                <span className="recent-count">{recentScores.length} logged</span>
              </div>
              {recentScores.length === 0 ? (
                <p className="no-scores">No recent runs in this browser session. Complete a test to see your score log!</p>
              ) : (
                <div className="recent-scores-grid">
                  {recentScores.map((score, index) => (
                    <div key={score.id || index} className="score-chip">
                      <span className="score-rank">#{index + 1}</span>
                      <span className="score-time">{formatTime(score.timeMs)}</span>
                      <span className="score-date">{score.date}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="leaderboard-view">
            <div className="leaderboard-header">
              <button className="back-btn" onClick={() => setActiveTab('type')}>
                <FaArrowLeft /> Back to Practice
              </button>
              <h2><FaTrophy className="gold-trophy" /> Leaderboard</h2>
              
              {/* Leaderboard Timeframe Tabs */}
              <div className="leaderboard-tabs">
                <button 
                  className={`tab-btn ${leaderboardPeriod === 'all' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('all')}
                >
                  <FaTrophy /> All Time
                </button>
                <button 
                  className={`tab-btn ${leaderboardPeriod === 'week' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('week')}
                >
                  <FaCalendarAlt /> This Week
                </button>
                <button 
                  className={`tab-btn ${leaderboardPeriod === 'today' ? 'active' : ''}`}
                  onClick={() => setLeaderboardPeriod('today')}
                >
                  <FaClock /> Today
                </button>
              </div>

              {!user && (
                <div className="guest-notice">
                  <p>💡 You are currently viewing as a guest. <strong>Sign in</strong> to save your high score to the global leaderboard!</p>
                </div>
              )}
            </div>

            {leaderboardLoading ? (
              <div className="leaderboard-loading">Loading top typists...</div>
            ) : leaderboard.length === 0 ? (
              <div className="empty-leaderboard">
                <p>No scores logged for this timeframe. Be the first player to set a score!</p>
              </div>
            ) : (
              <div className="leaderboard-table-wrapper">
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Best Time</th>
                      <th>Date / Time Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, idx) => (
                      <tr 
                        key={entry.id || idx} 
                        className={user && entry.userId === user.uid ? 'highlight-user' : ''}
                      >
                        <td className="rank-col">
                          {idx === 0 ? '🥇 1st' : idx === 1 ? '🥈 2nd' : idx === 2 ? '🥉 3rd' : `#${idx + 1}`}
                        </td>
                        <td className="player-col">
                          <AvatarImage photoURL={entry.photoURL} displayName={entry.displayName} />
                          <span className="player-name">{entry.displayName || 'Anonymous Typist'}</span>
                          {user && entry.userId === user.uid && (
                            <span className="you-badge">YOU</span>
                          )}
                        </td>
                        <td className="time-col">{formatTime(entry.timeMs)}</td>
                        <td className="date-col">
                          {formatLeaderboardDate(entry.createdAt, leaderboardPeriod)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'keybinds' && (
          <div className="keybinds-page-view">
            <div className="keybinds-page-header">
              <button className="back-btn" onClick={() => setActiveTab('type')}>
                <FaArrowLeft /> Back to Practice
              </button>
              <h2><FaSlidersH /> Keybind Configurations</h2>
              <p className="page-subtitle">Customize keyboard shortcuts for resetting the timer and deleting mistyped characters.</p>
            </div>

            <div className="keybinds-page-card">
              <div className="keybind-setting-row">
                <div className="setting-info">
                  <span className="setting-title">Reset Test Timer</span>
                  <span className="setting-desc">Instantly clears current attempt and resets the stopwatch.</span>
                </div>
                <button 
                  className={`keybind-bind-btn ${listeningForBind === 'reset' ? 'binding' : ''}`}
                  onClick={() => setListeningForBind('reset')}
                >
                  {listeningForBind === 'reset' ? 'Press key...' : keybinds.reset}
                </button>
              </div>

              <div className="keybind-setting-row">
                <div className="setting-info">
                  <span className="setting-title">Backspace / Delete Character</span>
                  <span className="setting-desc">Removes entered characters to correct mistyped letters.</span>
                </div>
                <button 
                  className={`keybind-bind-btn ${listeningForBind === 'backspace' ? 'binding' : ''}`}
                  onClick={() => setListeningForBind('backspace')}
                >
                  {listeningForBind === 'backspace' ? 'Press key...' : keybinds.backspace}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Auth Modal (Email/Password + Google Sign-In) */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{authIsSignUp ? 'Create Account' : 'Sign In'}</h3>
              <button className="close-btn" onClick={() => setShowAuthModal(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              {authError && <div className="modal-error-box">{authError}</div>}

              <form onSubmit={handleEmailAuthSubmit} className="auth-form">
                {authIsSignUp && (
                  <div className="form-group">
                    <label>Display Name</label>
                    <input 
                      type="text" 
                      value={authDisplayName}
                      onChange={(e) => setAuthDisplayName(e.target.value)}
                      placeholder="e.g. SpeedTypist"
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input 
                    type="password" 
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button type="submit" disabled={authSubmitting} className="auth-submit-btn">
                  {authSubmitting ? 'Processing...' : authIsSignUp ? 'Sign Up' : 'Sign In'}
                </button>
              </form>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <button 
                onClick={handleGoogleSignIn} 
                disabled={authSubmitting}
                className="google-modal-btn"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" />
                </svg>
                Sign in with Google
              </button>

              <button 
                type="button"
                className="toggle-auth-mode-btn"
                onClick={() => { setAuthIsSignUp(!authIsSignUp); setAuthError(''); }}
              >
                {authIsSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Required Subproject Footer */}
      <footer className="katz-footer">
        <a href="https://jordankatz.dev" target="_blank" rel="noopener noreferrer">
          A JORDAN KATZ PROJECT
        </a>
      </footer>
    </div>
  );
}

// Avatar image component with fallback error handler for Google PFP CORS/broken URLs
function AvatarImage({ photoURL, displayName }) {
  const [imgError, setImgError] = useState(false);

  if (!photoURL || imgError) {
    const initial = displayName ? displayName.charAt(0).toUpperCase() : 'U';
    return <div className="avatar-placeholder">{initial}</div>;
  }

  return (
    <img 
      src={photoURL} 
      alt={displayName || 'User'} 
      className="avatar" 
      onError={() => setImgError(true)}
      referrerPolicy="no-referrer"
    />
  );
}

export default App;
